/** A report as tables, for Excel (one sheet each) or CSV (one file, tables one after another). */
export interface ReportTable {
  /** Sheet name (Excel) and heading (CSV). */
  title: string;
  columns: string[];
  /** Numbers stay numbers (money in dollars, e.g. 125.5). */
  rows: (string | number | null)[][];
}

const safeName = (s: string) => s.replace(/[^A-Za-z0-9 _-]+/g, "").trim() || "report";

/** Download as an Excel workbook (.xlsx). Loaded only when someone exports. */
export async function downloadXlsx(fileName: string, tables: ReportTable[]) {
  const { default: writeXlsxFile } = await import("write-excel-file/browser");
  await writeXlsxFile(
    tables.map((t) => ({
      sheet: t.title.slice(0, 31),
      stickyRowsCount: 1,
      columns: t.columns.map((c, i) => ({ width: Math.max(12, Math.min(40, Math.max(c.length, ...t.rows.map((r) => String(r[i] ?? "").length)) + 2)) })),
      data: [
        t.columns.map((c) => ({ value: c, fontWeight: "bold" as const })),
        ...t.rows.map((r) => r.map((v) => (typeof v === "number" ? { value: v, type: Number, format: Number.isInteger(v) ? "0" : "#,##0.00" } : { value: v ?? "", type: String }))),
      ],
    })),
  ).toFile(`${safeName(fileName)}.xlsx`);
}

/** Download as CSV (opens in any spreadsheet). */
export function downloadCsv(fileName: string, tables: ReportTable[]) {
  const cell = (v: string | number | null) => {
    const s = v === null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replaceAll('"', '""')}"` : s;
  };
  const text = tables.map((t) => [cell(t.title), t.columns.map(cell).join(","), ...t.rows.map((r) => r.map(cell).join(","))].join("\r\n")).join("\r\n\r\n");
  const url = URL.createObjectURL(new Blob([`﻿${text}`], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `${safeName(fileName)}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Cents as dollars for a spreadsheet cell. */
export const dollars = (cents: number) => Math.round(cents) / 100 || 0;
